import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { OfflineProvider } from "@/context/OfflineContext";
import OfflineBanner from "@/components/OfflineBanner";
import '@/i18n'; // Initialize i18n

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import DemoLoginPage from "@/pages/DemoLoginPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import EstimatesPage from "@/pages/EstimatesPage";
import EstimateWizardPage from "@/pages/EstimateWizardPage";
import EstimateDetailPage from "@/pages/EstimateDetailPage";
import PublicEstimatePage from "@/pages/PublicEstimatePage";
import PublicJobStatusPage from "@/pages/PublicJobStatusPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceDetailPage from "@/pages/InvoiceDetailPage";
import PublicInvoicePage from "@/pages/PublicInvoicePage";
import JobsPage from "@/pages/JobsPage";
import SiteVisitsPage from "@/pages/SiteVisitsPage";
import SiteVisitDetailPage from "@/pages/SiteVisitDetailPage";
import FieldNotesPage from "@/pages/FieldNotesPage";
import SettingsPage from "@/pages/SettingsPage";
import SyncQueueRecoveryPage from "@/pages/SyncQueueRecoveryPage";
import EmergencyRecoverPage from "@/pages/EmergencyRecoverPage";
import AdminDashPage from "@/pages/AdminDashPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import PublicAdminInvoicePage from "@/pages/PublicAdminInvoicePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import HelpGuidePage from "@/pages/HelpGuidePage";
import LeadsPage from "@/pages/LeadsPage";
import JobDetailPage from "@/pages/JobDetailPage";

// Layout
import AppLayout from "@/components/layout/AppLayout";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground text-lg">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// App Routes
const AppRoutes = () => {
  const { isAuthenticated, needsOnboarding } = useAuth();
  
  return (
    <Routes>
      {/* Main page redirects to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* Marketing/Showcase Landing Page */}
      <Route path="/lp" element={<LandingPage />} />
      
      {/* Super Admin Route (separate from main app) */}
      <Route path="/admin-dash" element={<AdminDashPage />} />
      
      {/* Public Routes */}
      <Route path="/login" element={
        isAuthenticated && !needsOnboarding ? <Navigate to="/dashboard" replace /> : <LoginPage />
      } />
      <Route path="/demo" element={
        isAuthenticated && !needsOnboarding ? <Navigate to="/dashboard" replace /> : <DemoLoginPage />
      } />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/estimate/:token" element={<PublicEstimatePage />} />
      <Route path="/job-status/:token" element={<PublicJobStatusPage />} />
      <Route path="/invoice/view/:token" element={<PublicInvoicePage />} />
      <Route path="/auth/verify" element={<DemoLoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/portal/:token" element={<ClientPortalPage />} />
      <Route path="/admin-invoice/view/:token" element={<PublicAdminInvoicePage />} />
      
      {/* Protected Routes - App Section */}
      <Route element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:clientId" element={<ClientDetailPage />} />
        <Route path="/estimates" element={<EstimatesPage />} />
        <Route path="/estimates/new" element={<EstimateWizardPage />} />
        <Route path="/estimates/:estimateId" element={<EstimateDetailPage />} />
        <Route path="/estimates/:estimateId/edit" element={<EstimateWizardPage />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/jobs/:jobId" element={<JobDetailPage />} />
        <Route path="/site-visits" element={<SiteVisitsPage />} />
        <Route path="/site-visits/:visitId" element={<SiteVisitDetailPage />} />
        <Route path="/site-visits/:visitId/field-notes" element={<FieldNotesPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/:invoiceId" element={<InvoiceDetailPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/sync-queue" element={<SyncQueueRecoveryPage />} />
        <Route path="/emergency-recover" element={<EmergencyRecoverPage />} />
        <Route path="/help" element={<HelpGuidePage />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <OfflineProvider>
          <AuthProvider>
            <OfflineBanner />
            <AppRoutes />
            <Toaster 
              position="top-center" 
              expand={false}
              richColors={false}
              closeButton={true}
              duration={5000}
              gap={12}
              offset={20}
              visibleToasts={3}
            />
          </AuthProvider>
        </OfflineProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
